import { z } from 'zod';
import { ResolutionConfidenceBand } from './node-labels.js';
import { ToolIdentity } from './person.js';
export const ENTITY_RESOLUTION_CANDIDATES_COLLECTION = 'entityResolutionCandidates';
export const ENTITY_RESOLUTION_DECISIONS_COLLECTION = 'entityResolutionDecisions';
/**
 * Per-dimension breakdown of the similarity score the matcher
 * produced. Surfaced to the admin UI so the reviewer can see WHY two
 * identities scored where they did — opaque "we think 0.82" reads as
 * magic, but "email=0.0 / name=0.95 / handle=0.85 / avatar=0.7" is
 * legible.
 */
export const ResolutionScoreBreakdown = z.object({
    email: z.number().min(0).max(1),
    name: z.number().min(0).max(1),
    handle: z.number().min(0).max(1),
    avatar: z.number().min(0).max(1),
    /** The weighted composite score the matcher emits as the final number. */
    composite: z.number().min(0).max(1),
});
/**
 * A candidate merge between two identities. Created by the matcher
 * when the score lands in the `mid` band (>=0.7 and <=0.95). Stored in
 * `entityResolutionCandidates/{orgId}/{candidateId}` and queued for
 * admin review.
 *
 * High-band candidates auto-merge silently — they don't land here.
 * Low-band candidates stay separate but are listed in the admin
 * "flagged" view so an analyst can still merge them manually.
 *
 * `status` transitions are one-way: `pending` → `approved` (merge
 * applied) or `pending` → `rejected` (kept separate). The decision is
 * recorded as an `EntityResolutionDecision` for audit history.
 */
export const EntityResolutionCandidate = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** Existing Person id we'd merge INTO. */
    leftPersonId: z.string().min(1),
    /** The new identity (or existing Person) we'd merge in. */
    rightIdentity: ToolIdentity,
    /** Set when the right-hand side is an existing Person we'd merge with. */
    rightPersonId: z.string().optional(),
    band: ResolutionConfidenceBand,
    breakdown: ResolutionScoreBreakdown,
    status: z.enum(['pending', 'approved', 'rejected', 'auto-approved']),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Decision history. Every merge/split/approve/reject is recorded so
 * the graph can be re-played and so the admin UI can show "this was
 * merged on 2026-05-09 by @nima". Splits set `kind: 'split'` and
 * record both the source Person id and the resulting two Person ids
 * the split produced.
 */
export const ER_DECISION_KINDS = ['approve', 'reject', 'merge', 'split'];
export const EntityResolutionDecisionKind = z.enum(ER_DECISION_KINDS);
export const EntityResolutionDecision = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    kind: EntityResolutionDecisionKind,
    /** Candidate id when kind is approve/reject; absent for direct merge/split. */
    candidateId: z.string().optional(),
    /** Person id(s) involved — the surviving Person on merge, the source on split. */
    leftPersonId: z.string().min(1),
    rightPersonId: z.string().optional(),
    /** On split, the two resulting Person ids. */
    splitInto: z.tuple([z.string(), z.string()]).optional(),
    /**
     * On split, the ToolIdentity externalIds peeled into the new Person.
     * Persisting on the decision row (not just on the audit detail) so
     * the future Neo4j orchestrator can replay split intents without
     * having to join against the audit trail.
     */
    identitiesToSplit: z.array(z.string().min(1)).optional(),
    actorUid: z.string().min(1),
    reason: z.string().optional(),
    createdAt: z.string(),
});
/** Body for `POST /api/orgs/:o/graph/resolution/merge`. */
export const ManualMergeRequest = z.object({
    leftPersonId: z.string().min(1),
    rightPersonId: z.string().min(1),
    reason: z.string().max(500).optional(),
});
/** Body for `POST /api/orgs/:o/graph/resolution/split`. */
export const ManualSplitRequest = z.object({
    personId: z.string().min(1),
    /** Tool-identity externalIds to peel into the new Person. */
    identitiesToSplit: z.array(z.string().min(1)).min(1),
    reason: z.string().max(500).optional(),
});
